import { createContext, useCallback, useContext, useState } from 'react';
import { useConnectionContext } from './connections/ConnectionContext';
import { AssistantTabs } from './assistantTypes';

interface AssistantPanelContextProps {
  isOpen: boolean;
  openToTab: (tab: AssistantTabs) => void;
  close: () => void;
  requestedTab: AssistantTabs | undefined;
  clearRequestedTab: () => void;
}

const AssistantPanelContext = createContext<AssistantPanelContextProps | undefined>(undefined);

export function useAssistantPanelContext(): AssistantPanelContextProps {
  const context = useContext(AssistantPanelContext);
  if (context === undefined) {
    throw new Error('useAssistantPanelContext must be used within an AssistantPanelProvider');
  }
  return context;
}

export function AssistantPanelProvider({ children }: { children: React.ReactNode }): JSX.Element {
  const [isOpen, setIsOpen] = useState(false);
  const [requestedTab, setRequestedTab] = useState<AssistantTabs | undefined>();
  const { removeConnection } = useConnectionContext();

  const openToTab = useCallback((tab: AssistantTabs) => {
    setIsOpen(true);
    setRequestedTab(tab);
  }, []);

  const close = useCallback(() => {
    setIsOpen(false);
    setRequestedTab(undefined);
    removeConnection();
  }, [removeConnection]);

  const clearRequestedTab = useCallback(() => {
    setRequestedTab(undefined);
  }, []);

  return (
    <AssistantPanelContext.Provider value={{ isOpen, openToTab, close, requestedTab, clearRequestedTab }}>
      {children}
    </AssistantPanelContext.Provider>
  );
}
